import React, {
  FC,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import {
  Box,
  Button,
  Card,
  FormControl,
  Grid,
  InputLabel,
  makeStyles,
  MenuItem,
  Select,
  Typography
} from '@material-ui/core';
import { SearchKeyword } from 'src/components/filters';
import { CategoryListDropdown } from 'src/components/filters/CategoryDropdown';
import { BranchListDropDown } from 'src/components/dropdown';
import { DatePickerRangeComponent, SelectionChip } from 'src/components';
import { dateToday, transformerDateField } from 'src/utils';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import { PopoverNotification } from 'src/components/popover';
import { debounce, isEmpty } from 'lodash';
import { StockFilter } from 'src/enums/fast-moving-products';
import { slices, useAppSelector } from 'src/redux';
import { useSearchParams } from 'react-router-dom';
import { DateRangeSpecific } from 'src/types';

const useStyles = makeStyles((theme) => ({
  chip: {
    marginRight: theme.spacing(0.5),
    marginLeft: theme.spacing(0.5)
  }
}));

const { selectors: categorySelectors } = slices.category;

type onKeyDownAction = 'search' | 'category';

interface Props {
  loadingData: boolean;
  initialBranchIds?: number[];
  categoryRequire: boolean;
  dateRangeRequire: boolean;
  branchRequire: boolean;
  onApplyFilter: () => void;
  onHandleSearchKeyword: (keyword?: string) => void;
  onHandleCategoryId: (categortId: number) => void;
  onHandleBranch: (branchIds?: number[]) => void;
  onHandleDateFrom: (dateFrom?: string | undefined) => void;
  onHandleDateTo: (dateTo?: string | undefined) => void;
  onHandleErrorClose: () => void;
  onChangeStatusFilter: (val: StockFilter) => void;
  onHandlePresetDate: (val?: DateRangeSpecific | string) => void;
}

const component: FC<Props> = ({
  loadingData,
  initialBranchIds,
  categoryRequire,
  dateRangeRequire,
  branchRequire,
  onApplyFilter,
  onHandleSearchKeyword,
  onHandleCategoryId,
  onHandleBranch,
  onHandleDateFrom,
  onHandleDateTo,
  onHandleErrorClose,
  onChangeStatusFilter,
  onHandlePresetDate
}) => {
  const categoryFieldRef = useRef(null);
  const dateRangeFieldRef = useRef(null);
  const branchFieldRef = useRef(null);
  const categories = useAppSelector(categorySelectors.selectCategories);
  const classes = useStyles();

  const [searchParams] = useSearchParams();

  const [fromDate, setFromDate] = useState<string | undefined>();
  const [toDate, setToDate] = useState<string | undefined>();
  const [isClearSearchKeyword, setIsClearSearchKeyword] = useState<boolean>(
    false
  );
  const [isCategory, setIsCategory] = useState<boolean>(false); //check if category is set
  const [isSearchKeyword, setIsSearchKeyword] = useState<string>(''); //check if keyword is set
  const [isKeyDown, setIsKeyDown] = useState<boolean>(false); //to avoid spamming keys
  const [presetDate, setPresetDate] = useState<
    DateRangeSpecific | undefined | string
  >();

  const onChangePreset = (val: DateRangeSpecific) => {
    const newPreset = val === presetDate ? undefined : val;
    setPresetDate(newPreset);

    // clear date sold ui when setting preset dates no matter what
    setFromDate(undefined);
    setToDate(undefined);

    onHandlePresetDate(newPreset);
  };

  const queryParams = useMemo(() => {
    return Object.fromEntries(searchParams.entries());
  }, [searchParams]);

  const stockFilterOptions = [
    { name: 'WITH/WITHOUT SALES', value: StockFilter.All },
    { name: 'WITH SALES', value: StockFilter.WithSales }
  ];

  const init_category = useMemo(
    () =>
      categories?.find(
        (categ) => categ?.id?.toString() === queryParams?.category_id
      ),
    [queryParams, categories]
  );

  const onChangedDate = useCallback(
    (date: MaterialUiPickersDate | undefined, field: 'from' | 'to') => {
      const transformedDate = date ? transformerDateField(date) : undefined;
      setPresetDate(undefined); // clear preset ui when date is present

      if (field === 'to') {
        setToDate(transformedDate);
        onHandleDateTo(transformedDate);
      }
      if (field === 'from') {
        setFromDate(transformedDate);
        onHandleDateFrom(transformedDate);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onHandleDateFrom, onHandleDateTo]
  );

  const handleCategoryChange = (categoryId: number) => {
    onHandleCategoryId(categoryId);
    setIsClearSearchKeyword(true);

    delete queryParams.keyword;

    if (categoryId) {
      setIsCategory(true);
    } else {
      setIsCategory(false);
    }
  };

  const onChangeKeyword = (keyword?: string) => {
    onHandleSearchKeyword(keyword);
    setIsSearchKeyword(keyword ?? '');
    if (isEmpty(keyword)) {
      setIsClearSearchKeyword(false);
    }
  };

  const onHandleKeyDown = (
    event: React.KeyboardEvent<HTMLDivElement>,
    action?: onKeyDownAction
  ) => {
    //no category no onkeydown event
    if (!isCategory && action === 'category') {
      return;
    }
    //no keyword no onkeydown event
    if (!isSearchKeyword && action === 'search') {
      return;
    }
    if (event.key === 'Enter' && !loadingData && !isKeyDown) {
      setIsKeyDown(true);
      debounce(() => {
        onApplyFilter();
        setIsKeyDown(false);
      }, 500)();
    }
  };

  const isValidDateString = (value?: string | null): boolean => {
    if (!value) return false;
    const date = new Date(value);
    return !isNaN(date.getTime());
  };

  const availabilityValue = useMemo(() => {
    if (queryParams.with_sales === '0') return StockFilter.All;
    if (queryParams.with_sales === '1') return StockFilter.WithSales;

    return StockFilter.All;
  }, [queryParams.with_sales]);

  useEffect(() => {
    const { date_sold_from, date_sold_to, preset_dates, keyword } = queryParams;

    if (isValidDateString(date_sold_from)) {
      setFromDate(date_sold_from);
      onHandleDateFrom(date_sold_from);
    }

    if (isValidDateString(date_sold_to)) {
      setToDate(date_sold_to);
      onHandleDateTo(date_sold_to);
    }

    setIsSearchKeyword(keyword ?? '');
    if (preset_dates) {
      setPresetDate(preset_dates);
      onHandlePresetDate(preset_dates);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    queryParams.date_sold_from,
    queryParams.date_sold_to,
    queryParams.keyword
  ]);

  return (
    <Card style={{ padding: '2rem' }}>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <div onKeyDown={(event) => onHandleKeyDown(event, 'search')}>
            <SearchKeyword
              value={isSearchKeyword}
              isClearEvent={isClearSearchKeyword}
              onHandleSearchKeyword={(keyword: string) =>
                onChangeKeyword(keyword)
              }
            />
          </div>
        </Grid>
        <Grid item xs={12} md={6}>
          <CategoryListDropdown
            defaultValue={init_category}
            onHandleCategories={(categoryId: number) =>
              handleCategoryChange(categoryId)
            }
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <div ref={branchFieldRef}>
            <BranchListDropDown
              defaultValue={initialBranchIds}
              isElevated={false}
              multiple
              paperSpace={0}
              onHandleBranchChange={(branchIds?: number[]) =>
                onHandleBranch(branchIds)
              }
            />
          </div>
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl variant="outlined" fullWidth>
            <InputLabel id="Availability">Availability</InputLabel>
            <Select
              defaultValue={availabilityValue || StockFilter.All}
              onChange={(e: any) => onChangeStatusFilter(e.target.value)}
              label="Availability"
            >
              {stockFilterOptions.map((i) => (
                <MenuItem key={i.name} value={i.value}>
                  {i.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            flexDirection: 'column'
          }}
        >
          <div ref={dateRangeFieldRef}>
            <DatePickerRangeComponent
              title="Date Sold Range"
              fromDateMax={dateToday()}
              fromDateValue={fromDate || null}
              toDateValue={toDate || null}
              dateToLabel="End Date"
              dateFromLabel="Start Date"
              toDateMin={fromDate}
              onChangeToDate={(date) => onChangedDate(date, 'to')}
              onChangeFromDate={(date) => onChangedDate(date, 'from')}
            />
          </div>
          <Box
            className="presetFilters"
            style={{
              textAlign: 'center',
              marginTop: '1em'
            }}
          >
            <Typography variant="h5" style={{ marginBottom: '1em' }}>
              Preset Filters
            </Typography>

            <Grid container spacing={2} style={{ marginBottom: '1em' }}>
              <Grid
                item
                md={3}
                sm={3}
                xs={6}
                style={{ display: 'flex', justifyContent: 'center' }}
              >
                <SelectionChip
                  label="Yesterday"
                  className={classes.chip}
                  isSelected={presetDate === 'yesterday'}
                  onClick={() => onChangePreset('yesterday')}
                  width={'92px'}
                />
              </Grid>
              <Grid
                item
                md={3}
                sm={3}
                xs={6}
                style={{ display: 'flex', justifyContent: 'center' }}
              >
                <SelectionChip
                  label="Today"
                  className={classes.chip}
                  isSelected={presetDate === 'today'}
                  onClick={() => onChangePreset('today')}
                  width={'92px'}
                />
              </Grid>
              <Grid
                item
                md={3}
                sm={3}
                xs={6}
                style={{ display: 'flex', justifyContent: 'center' }}
              >
                <SelectionChip
                  label="This Week"
                  className={classes.chip}
                  isSelected={presetDate === 'week'}
                  onClick={() => onChangePreset('week')}
                  width={'92px'}
                />
              </Grid>
              <Grid
                item
                sm={3}
                md={3}
                xs={6}
                style={{ display: 'flex', justifyContent: 'center' }}
              >
                <SelectionChip
                  label="This Month"
                  className={classes.chip}
                  isSelected={presetDate === 'month'}
                  onClick={() => onChangePreset('month')}
                  width={'92px'}
                />
              </Grid>
            </Grid>
          </Box>
          <Box>
            <Button
              variant="contained"
              color={'primary'}
              onClick={onApplyFilter}
              disabled={loadingData}
            >
              Apply Filter
            </Button>
          </Box>
        </Box>
      </Grid>

      <PopoverNotification
        severity={'error'}
        textInfo="Category is required"
        isOpen={categoryRequire}
        anchorRef={categoryFieldRef.current ?? undefined}
        onHandleClose={onHandleErrorClose}
      />
      <PopoverNotification
        severity={'error'}
        textInfo="Sold date from and to is required"
        isOpen={dateRangeRequire}
        anchorRef={dateRangeFieldRef.current ?? undefined}
        onHandleClose={onHandleErrorClose}
      />

      <PopoverNotification
        severity={'error'}
        textInfo="Branch field is required"
        isOpen={branchRequire}
        anchorRef={branchFieldRef.current ?? undefined}
        onHandleClose={onHandleErrorClose}
      />
    </Card>
  );
};

export const FastMovingProductToolBar = memo(component);
